import type { QueryClient } from "@tanstack/react-query";

/**
 * Query keys, and the slice map that drives invalidation.
 *
 * Almost every read in Exognitos is a composite: Today reads tasks, schedule,
 * routines and focus; Insights reads nearly everything. Invalidating precisely
 * would mean each mutation knowing which of a dozen screens it affects — and
 * the day one of them is forgotten, two surfaces start disagreeing about the
 * same database.
 *
 * So writes declare which *slices* of the domain they touched, and the map
 * below turns that into the set of caches to refetch. It is the same
 * coarse-grained reactivity the Flutter client settled on, for the same reason:
 * a personal dataset is small enough that a slightly wide refetch is
 * imperceptible, and correctness is not.
 */

export type Slice =
  | "pages"
  | "session"
  | "projects"
  | "tasks"
  | "routines"
  | "schedule"
  | "focus"
  | "inbox"
  | "notes"
  | "compass"
  | "activity"
  | "memory"
  | "knowledge"
  | "meaning"
  | "planning"
  | "life"
  | "relations";

export const qk = {
  session: ["session"] as const,
  preferences: ["preferences"] as const,
  tokens: ["api-tokens"] as const,
  totpStatus: ["totp-status"] as const,
  syncCursor: (accountId: string) => ["sync", "cursor", accountId] as const,

  projects: (filters?: Record<string, unknown>) => ["projects", filters ?? {}] as const,
  projectsOverview: (filters?: Record<string, unknown>) =>
    ["projects-overview", filters ?? {}] as const,
  project: (id: string) => ["project", id] as const,
  projectDetail: (id: string) => ["project-detail", id] as const,
  goals: (filters?: Record<string, unknown>) => ["goals", filters ?? {}] as const,
  decisions: (projectId?: string) => ["decisions", projectId ?? null] as const,
  tasks: (filters?: Record<string, unknown>) => ["tasks", filters ?? {}] as const,
  task: (id: string) => ["task", id] as const,
  taskDetail: (id: string) => ["task-detail", id] as const,

  routines: (filters?: Record<string, unknown>) => ["routines", filters ?? {}] as const,
  routine: (id: string) => ["routine", id] as const,
  routineDetail: (id: string) => ["routine-detail", id] as const,
  routineEffort: (id: string, from: string, to: string) =>
    ["routine-effort", id, from, to] as const,
  routineProjection: (id: string, from: string, to: string) =>
    ["routine-projection", id, from, to] as const,

  schedule: (from: string, to: string) => ["schedule", from, to] as const,

  day: (date: string) => ["day", date] as const,
  now: ["now"] as const,
  whatNow: (input: Record<string, unknown>) => ["what-now", input] as const,
  openLoops: ["open-loops"] as const,
  closure: (date: string) => ["closure", date] as const,
  range: (from: string, to: string) => ["range", from, to] as const,

  focusActive: ["focus-active"] as const,
  focusHistory: (filters?: Record<string, unknown>) => ["focus-history", filters ?? {}] as const,
  focusInterruptions: (sessionId: string) => ["focus-active", "interruptions", sessionId] as const,

  inbox: (filters?: Record<string, unknown>) => ["inbox", filters ?? {}] as const,
  inboxCount: ["inbox-count"] as const,

  notes: (filters?: Record<string, unknown>) => ["notes", filters ?? {}] as const,
  note: (id: string) => ["note", id] as const,
  noteRevisions: (id: string) => ["note", id, "revisions"] as const,
  compass: ["compass"] as const,
  journal: (filters?: Record<string, unknown>) => ["journal", filters ?? {}] as const,
  journalEntry: (id: string) => ["journal", "entry", id] as const,
  reviewDeck: (filters?: Record<string, unknown>) => ["review-deck", filters ?? {}] as const,
  weekly: ["weekly-review"] as const,

  search: (query: string, kinds?: string[]) => ["search", query, kinds ?? []] as const,
  memory: ["memory"] as const,
  memoryFeed: ["memory", "feed"] as const,
  memoryTimeline: ["memory", "timeline"] as const,

  dueReminders: ["due-reminders"] as const,
  pushConfig: ["push-config"] as const,
} as const;

/** Root key prefixes that a given slice makes stale. */
const SLICE_KEYS: Record<Slice, string[]> = {
  pages: ["pages"],
  session: ["session", "preferences"],
  projects: [
    "projects",
    "projects-overview",
    "project",
    "project-detail",
    "goals",
    "decisions",
    "day",
    "now",
    "open-loops",
    "current-context",
    "review-deck",
    "weekly-review",
    "insights",
    "search",
  ],
  tasks: [
    "tasks",
    "task",
    "task-detail",
    "task-deferrals",
    "day",
    "now",
    "what-now",
    "open-loops",
    "current-context",
    "closure",
    "range",
    "project-detail",
    "projects-overview",
    "routines",
    "routine",
    "routine-detail",
    "routine-effort",
    "review-deck",
    "weekly-review",
    "insights",
    "search",
  ],
  routines: [
    "routines",
    "routine",
    "routine-detail",
    "day",
    "current-context",
    "now",
    "what-now",
    "closure",
    "range",
    "project-detail",
    "insights",
    "weekly-review",
    "review-deck",
    "search",
  ],
  schedule: [
    "schedule",
    "day",
    "current-context",
    "now",
    "what-now",
    "closure",
    "range",
    "open-loops",
    "tasks",
    "task",
    "task-detail",
    "project-detail",
    "projects-overview",
    "insights",
    "estimate",
    "weekly-review",
    "due-reminders",
  ],
  focus: [
    "focus-active",
    "focus-history",
    "focus-interruptions",
    "day",
    "current-context",
    "closure",
    "range",
    "insights",
    "estimate",
    "project-detail",
    "projects-overview",
    "task-detail",
    "compass",
    "weekly-review",
  ],
  inbox: [
    "inbox",
    "inbox-count",
    "open-loops",
    "current-context",
    "review-deck",
    "weekly-review",
    "search",
  ],
  notes: ["notes", "note", "note-revisions", "project-detail", "review-deck", "search"],
  compass: ["compass", "journal", "current-context", "support-sessions", "range", "insights"],
  activity: ["closure", "day", "weekly-review", "project-detail", "projects-overview", "insights"],
  memory: ["memory", "notes", "inbox", "search"],
  knowledge: ["knowledge-network"],
  meaning: ["meaning", "day", "current-context", "project-detail", "insights", "search"],
  planning: [
    "planning",
    "periods",
    "schedule",
    "day",
    "current-context",
    "now",
    "what-now",
    "open-loops",
    "insights",
  ],
  life: ["life", "life-areas", "life-schema", "life-items", "life-day", "day", "search"],
  relations: ["relations", "entity-graph", "entity-links", "life", "search"],
};

export function invalidate(client: QueryClient, ...slices: Slice[]): Promise<void> {
  const roots = new Set<string>();
  for (const slice of slices) {
    for (const key of SLICE_KEYS[slice]) roots.add(key);
  }
  return Promise.all([...roots].map((root) => client.invalidateQueries({ queryKey: [root] }))).then(
    () => undefined,
  );
}

/** Keep the two global lifecycle surfaces coherent with domain-level mutations. */
export function invalidateLifecycle(client: QueryClient, ...slices: Slice[]): Promise<void> {
  return Promise.all([
    invalidate(client, ...slices),
    client.invalidateQueries({ queryKey: ["archive"] }),
    client.invalidateQueries({ queryKey: ["trash"] }),
  ]).then(() => undefined);
}
